import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import apiClient from '../../services/apiClient'
import { CoachModel } from '../../types/coach.type'
import './CoachMultiSelect.css'

interface CoachMultiSelectProps {
    selectedCoachIds: number[]
    onChange: (coachIds: number[]) => void
    disabled?: boolean
    className?: string
    excludeCoachId?: number | null
}

const CoachMultiSelect: React.FC<CoachMultiSelectProps> = ({
    selectedCoachIds = [],
    onChange,
    disabled = false,
    className = '',
    excludeCoachId = null,
}) => {
    const { t } = useTranslation()
    const [inputValue, setInputValue] = useState('')

    // Récupérer la liste des coachs
    const { data: coaches = [], isLoading } = useQuery<CoachModel[]>({
        queryKey: ['coaches'],
        queryFn: async () => {
            const response = await apiClient.get('/coachs')
            return response.data
        },
        staleTime: 24 * 60 * 60 * 1000, // 1 jour
    })

    // Filtrer les coachs pour exclure le coach spécifié
    const filteredCoaches = excludeCoachId
        ? coaches.filter((coach: CoachModel) => coach.id !== excludeCoachId)
        : coaches

    // Préparer les options pour react-select
    const options = filteredCoaches.map((coach: CoachModel) => ({
        value: coach.id,
        label: `${coach.firstName} (${coach.email})`.trim(),
    }))

    // Convertir les IDs sélectionnés en format attendu par react-select
    const selectedOptions = options.filter((option: { value: number }) => selectedCoachIds.includes(option.value))

    const handleChange = (selectedOptions: any) => {
        const selectedIds = selectedOptions ? selectedOptions.map((option: { value: number }) => option.value) : []
        onChange(selectedIds)
    }

    // Personnalisation du style de react-select pour correspondre au thème
    const customStyles = {
        control: (provided: any) => ({
            ...provided,
            minHeight: '38px',
            borderColor: '#ced4da',
            '&:hover': {
                borderColor: '#80bdff',
                boxShadow: '0 0 0 0.2rem rgba(0, 123, 255, 0.25)',
            },
        }),
        multiValue: (provided: any) => ({
            ...provided,
            backgroundColor: '#e9ecef',
            borderRadius: '4px',
        }),
        multiValueLabel: (provided: any) => ({
            ...provided,
            color: '#495057',
            padding: '2px 6px',
        }),
        multiValueRemove: (provided: any) => ({
            ...provided,
            color: '#6c757d',
            ':hover': {
                backgroundColor: '#dee2e6',
                color: '#495057',
            },
        }),
    }

    if (isLoading) {
        return (
            <Form.Group className={`mb-3 ${className}`}>
                <Form.Label>{t('coach.trainingSessionGroups.shareWithCoaches')}</Form.Label>
                <div className="form-control" style={{ minHeight: '38px', padding: '6px 12px' }}>
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </div>
                    <span className="ms-2">Chargement des coachs...</span>
                </div>
            </Form.Group>
        )
    }

    return (
        <Form.Group className={`mb-3 ${className}`}>
            <Form.Label>{t('coach.trainingSessionGroups.shareWithCoaches')}</Form.Label>
            <Select
                isMulti
                options={options}
                value={selectedOptions}
                onChange={handleChange}
                isDisabled={disabled || isLoading}
                placeholder="Rechercher des coachs..."
                noOptionsMessage={() => 'Aucun coach trouvé'}
                loadingMessage={() => 'Chargement...'}
                className="react-select-container"
                classNamePrefix="react-select"
                styles={customStyles}
                inputValue={inputValue}
                onInputChange={(newValue) => setInputValue(newValue)}
                components={{
                    DropdownIndicator: null,
                }}
            />
            <Form.Text className="text-muted">{t('coach.trainingSessionGroups.shareWithCoachesHelp')}</Form.Text>
        </Form.Group>
    )
}

export default CoachMultiSelect
